import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Table, Button, 
  Modal, Form, Alert, Spinner, Badge 
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './components/AdminNavbar';
import { authAPI, departmentsAPI } from '../../services/api';

const Departments = () => {
  const [user, setUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit'
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    hod_name: '',
    hod_email: '',
    hod_contact: '',
    building: '',
    floor: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const currentUser = authAPI.getCurrentUser();
    if (!currentUser) {
      navigate('/admin/login');
      return;
    }
    setUser(currentUser);
    fetchDepartments();
  }, [navigate]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await departmentsAPI.getAll();
      
      if (result && result.success === true) {
        setDepartments(result.data || []);
      } else {
        setError(result?.message || 'Failed to fetch departments');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  const handleAddClick = () => {
    setModalType('add');
    setFormData({
      name: '',
      code: '',
      description: '',
      hod_name: '',
      hod_email: '',
      hod_contact: '',
      building: '',
      floor: ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditClick = (department) => {
    setModalType('edit');
    setSelectedDepartment(department);
    setFormData({
      name: department.name || '',
      code: department.code || '',
      description: department.description || '',
      hod_name: department.hod_name || '',
      hod_email: department.hod_email || '',
      hod_contact: department.hod_contact || '',
      building: department.building || '',
      floor: department.floor || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteClick = (department) => {
    setDeleteConfirm(department);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const result = await departmentsAPI.delete(deleteConfirm.id);
      
      if (result && result.success === true) {
        setSuccess('Department deleted successfully');
        fetchDepartments();
      } else {
        setError(result?.message || 'Failed to delete department');
      }
    } catch (error) {
      setError(error.message || 'Delete failed');
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Department name is required';
    }
    
    if (formData.hod_email && !/\S+@\S+\.\S+/.test(formData.hod_email)) {
      errors.hod_email = 'Invalid email address';
    }
    
    if (formData.hod_contact && !/^[\d\s+\-()]{10,}$/.test(formData.hod_contact)) {
      errors.hod_contact = 'Invalid contact number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      let result;
      
      if (modalType === 'add') {
        result = await departmentsAPI.create(formData);
      } else {
        result = await departmentsAPI.update(selectedDepartment.id, formData);
      }
      
      if (result && result.success === true) {
        setSuccess(`Department ${modalType === 'add' ? 'added' : 'updated'} successfully`);
        setShowModal(false);
        fetchDepartments();
      } else {
        setError(result?.message || `Failed to ${modalType} department`);
      }
    } catch (error) {
      setError(error.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNavbar onLogout={handleLogout} user={user} />
      
      <Container fluid className="py-4 admin-content">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-0">
              <i className="fas fa-sitemap me-2 text-primary"></i>
              Department Management
            </h2>
            <p className="text-muted mb-0">Manage college departments</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={handleAddClick}>
              <i className="fas fa-plus me-2"></i>
              Add New Department
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={fetchDepartments}
              className="ms-2"
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </Button>
          </Col>
        </Row>

        {/* Messages */}
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {/* Stats Card */}
        <Row className="mb-4">
          <Col lg={3}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-sitemap fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">{departments.length}</h3>
                    <p className="mb-0">Total Departments</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="fas fa-user-tie fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="mb-0">
                      {departments.filter(d => d.hod_name).length}
                    </h3>
                    <p className="mb-0">HODs Assigned</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Departments Table */}
        <Card className="shadow">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading departments...</p>
              </div>
            ) : departments.length > 0 ? (
              <div className="table-responsive">
                <Table hover striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Department</th>
                      <th>Code</th>
                      <th>HOD</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((dept) => (
                      <tr key={dept.id}>
                        <td>{dept.id}</td>
                        <td>
                          <strong>{dept.name}</strong>
                          {dept.description && (
                            <small className="d-block text-muted" style={{fontSize: '0.85em'}}>
                              {dept.description.length > 50 
                                ? `${dept.description.substring(0, 50)}...` 
                                : dept.description}
                            </small>
                          )}
                        </td>
                        <td>
                          {dept.code ? (
                            <Badge bg="info" className="px-2 py-1">
                              {dept.code}
                            </Badge>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>
                          {dept.hod_name ? (
                            <>
                              <div>{dept.hod_name}</div>
                              {dept.hod_email && (
                                <small className="text-muted">{dept.hod_email}</small>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">Not assigned</span>
                          )}
                        </td>
                        <td>
                          {dept.hod_contact || <span className="text-muted">N/A</span>}
                        </td>
                        <td>
                          {dept.building && (
                            <div>
                              <i className="fas fa-building me-1"></i>
                              {dept.building}
                              {dept.floor && `, Floor ${dept.floor}`}
                            </div>
                          )}
                        </td>
                        <td>
                          {formatDate(dept.created_at)}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleEditClick(dept)}
                              disabled={loading}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Edit
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteClick(dept)}
                              disabled={loading}
                            >
                              <i className="fas fa-trash me-1"></i>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="fas fa-sitemap fa-3x text-muted mb-3"></i>
                <h5>No departments found</h5>
                <p className="text-muted mb-4">Add your first department to get started</p>
                <Button variant="primary" onClick={handleAddClick}>
                  <i className="fas fa-plus me-2"></i>
                  Add Department
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-sitemap me-2"></i>
            {modalType === 'add' ? 'Add New Department' : 'Edit Department'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Department Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="e.g., Computer Science"
                    value={formData.name}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    placeholder="e.g., CS"
                    value={formData.code}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Short code for the department (optional)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                placeholder="Department description..."
                value={formData.description}
                onChange={handleInputChange}
              />
            </Form.Group>

            <h6 className="border-bottom pb-2 mb-3">
              <i className="fas fa-user-tie me-2"></i>
              Head of Department Details
            </h6>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HOD Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="hod_name"
                    placeholder="e.g., Dr. John Smith"
                    value={formData.hod_name}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HOD Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="hod_email"
                    placeholder="e.g., hod.cs@college.edu"
                    value={formData.hod_email}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.hod_email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.hod_email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HOD Contact</Form.Label>
                  <Form.Control
                    type="text"
                    name="hod_contact"
                    placeholder="e.g., +91 9876543210"
                    value={formData.hod_contact}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.hod_contact}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.hod_contact}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <h6 className="border-bottom pb-2 mb-3">
              <i className="fas fa-building me-2"></i>
              Location Details
            </h6>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Building</Form.Label>
                  <Form.Control
                    type="text"
                    name="building"
                    placeholder="e.g., Main Building, Science Block"
                    value={formData.building}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Floor</Form.Label>
                  <Form.Control
                    type="text"
                    name="floor"
                    placeholder="e.g., 2nd Floor, Ground Floor"
                    value={formData.floor}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalType === 'add' ? 'Adding...' : 'Updating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  {modalType === 'add' ? 'Add Department' : 'Update Department'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the department:</p>
          <h5 className="text-center my-3">
            <Badge bg="secondary" className="p-2 fs-5">{deleteConfirm?.name}</Badge>
          </h5>
          {deleteConfirm?.code && (
            <p className="text-center">
              <Badge bg="info" className="px-3 py-1">
                Code: {deleteConfirm.code}
              </Badge>
            </p>
          )}
          <p className="text-danger small">
            <i className="fas fa-exclamation-circle me-1"></i>
            This action cannot be undone. All related data will be removed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete} 
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2"></i>
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Departments;